/* Lunai search — a bottom sheet on phones, a frosted panel on desktop.

   Zero state (before a keystroke): favourites · recent · moments.
   As-you-type: Shopify's Predictive Search API, rendered server-side by
   sections/lunai-search-suggest.liquid (query completions · collections ·
   products). Committed search: the real /search page, which owns facets.

   Why predictive rather than the full-text /search this file used to call:
   measured 2026-08-13, "povodc" (one typo) returns ZERO products from full
   text but the right ones from predictive, which is also ~1.5× faster. */
(function () {
  if (customElements.get('lunai-search')) return;

  var RECENT_KEY = 'lunai-recent';
  var FAV_KEY = 'lunai-wishlist';
  /* Handle→variant map, written by lunai-favorites.js: which colourway
     the shopper hearted. Read-only here — the sheet renders favourites,
     it never toggles them. */
  var FAV_VKEY = 'lunai-wishlist-v';
  /* Locale is part of the key: the cached value is rendered markup, so a
     shopper who switches language would be served the previous locale's
     cards for the rest of the session. The per-card key is
     FAVCACHE + handle + (':' + variantId when one is stored). Kept
     identical in assets/lunai-favorites.js, which shares this cache. */
  var FAVCACHE = 'lunai-favcard-' + ((window.Shopify && window.Shopify.locale) || '') + '-';
  var FAV_LIMIT = 8;
  var SUGGEST_LIMIT = 6;

  /* ⌘K / Ctrl-K opens the header search from anywhere. */
  document.addEventListener('keydown', function (e) {
    if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
      var details = document.querySelector('.lunai-search details');
      if (!details) return;
      e.preventDefault();
      /* Go through DetailsModal's lifecycle (scroll lock, trapFocus) —
         toggling details.open directly bypassed both. */
      var dm = details.closest('details-modal');
      if (details.open) {
        if (dm && typeof dm.close === 'function') dm.close();
        else details.open = false;
      } else {
        var summary = details.querySelector('summary');
        if (summary) summary.click();
        else details.open = true;
      }
    }
  });

  function shopRoot() {
    return (window.routes && routes.root_url && routes.root_url !== '/')
      ? routes.root_url.replace(/\/$/, '')
      : '';
  }

  /* Fold diacritics so one locale entry covers both spellings a Slovenian
     phone keyboard produces — "ščetka" and "scetka" are the same miss. */
  function fold(s) {
    return (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }

  /* "ogrlica>ovratnica|miska>posoda" → { ogrlica: 'ovratnica', … }, keyed
     folded. Values keep their diacritics: they are handed to Shopify. */
  function parseMap(raw) {
    var out = {};
    (raw || '').split('|').forEach(function (pair) {
      var i = pair.indexOf('>');
      if (i < 1) return;
      var k = fold(pair.slice(0, i).trim());
      var v = pair.slice(i + 1).trim();
      if (k && v) out[k] = v;
    });
    return out;
  }

  function parseSet(raw) {
    var out = {};
    (raw || '').split(',').forEach(function (w) {
      var k = fold(w.trim());
      if (k) out[k] = true;
    });
    return out;
  }

  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  class LunaiSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('[data-lsr-input]');
      this.results = this.querySelector('[data-lsr-results]');
      this.empty = this.querySelector('[data-lsr-empty]');
      this.clearBtn = this.querySelector('[data-lsr-clear]');
      this.escBtn = this.querySelector('.lunai-search__esc');
      this.recentWrap = this.querySelector('[data-lsr-recent]');
      this.recentTags = this.querySelector('[data-lsr-recent-tags]');
      this.recentClear = this.querySelector('[data-lsr-recent-clear]');
      this.note = this.querySelector('[data-lsr-note]');
      this.suggestSection = this.dataset.suggestSection || 'lunai-search-suggest';
      this.fullSection = this.dataset.sectionId || 'lunai-search-results';
      this.synonyms = parseMap(this.dataset.synonyms);
      this.stopwords = parseSet(this.dataset.stopwords);
      this.generic = parseSet(this.dataset.genericTerms);
      this.idx = -1;
      this.timer = null;
      this.controller = null;
    }

    connectedCallback() {
      if (this._wired || !this.input) return;
      this._wired = true;
      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('keydown', this.onKey.bind(this));
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', function () {
          this.input.value = '';
          this.onInput();
          this.input.focus();
        }.bind(this));
      }
      if (this.recentClear) {
        this.recentClear.addEventListener('click', function () {
          try { localStorage.removeItem(RECENT_KEY); } catch (e) {}
          this.renderRecent();
          this.input.focus();
        }.bind(this));
      }
      if (this.escBtn) this.escBtn.addEventListener('click', this.close.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.renderRecent();
      this.renderFavorites();

      var details = this.closest('details');
      if (details) {
        new MutationObserver(function () {
          if (details.open) {
            this.renderRecent();
            this.renderFavorites();
            this.trackKeyboard();
            this.initDrag();
            /* Clear any transform a previous drag left behind, so the
               sheet never reopens part-way down the screen. */
            var sheet = this.closest('.search-modal__content');
            if (sheet) { sheet.classList.remove('is-settling'); sheet.style.transform = ''; }
            setTimeout(function () { this.input.focus(); }.bind(this), 60);
          } else {
            this.untrackKeyboard();
          }
        }.bind(this)).observe(details, { attributes: true, attributeFilter: ['open'] });
      }
    }

    /* ── Swipe-to-dismiss ────────────────────────────────────────────
       Flick the sheet down to close it. The whole difficulty is telling a
       dismiss from a scroll, and the rule is decided ONCE per gesture, on
       the first move: a downward drag counts as dismiss only when the
       results underneath are already at the top, so a half-scrolled list
       keeps scrolling exactly as before. The bar is excluded so dragging
       near the input can never throw the sheet away mid-typing.

       Enhancement only — Esc, the overlay tap and the ESC chip are
       untouched, and nothing here runs without touch. */
    initDrag() {
      var sheet = this.closest('.search-modal__content');
      if (!sheet || this._dragWired) return;
      this._dragWired = true;

      var self = this;
      var mq = window.matchMedia('(max-width: 989px)');
      var startY = 0, delta = 0, tracking = false, decided = false, dismissing = false;
      var scroller = null;

      var reset = function () {
        tracking = decided = dismissing = false;
        delta = 0;
        scroller = null;
      };

      var settle = function (toClose) {
        sheet.classList.add('is-settling');
        if (toClose) {
          sheet.style.transform = 'translateY(100%)';
          /* Let the slide finish before DetailsModal yanks the panel, so
             the sheet leaves rather than vanishing mid-gesture. */
          setTimeout(function () {
            self.close();
            sheet.classList.remove('is-settling');
            sheet.style.transform = '';
          }, 190);
        } else {
          sheet.style.transform = '';
          setTimeout(function () { sheet.classList.remove('is-settling'); }, 220);
        }
      };

      sheet.addEventListener('touchstart', function (e) {
        if (!mq.matches || e.touches.length !== 1) return;
        if (e.target.closest('.lunai-search__bar')) return;
        scroller = e.target.closest('.lunai-search__body');
        startY = e.touches[0].clientY;
        tracking = true;
        decided = false;
        dismissing = false;
      }, { passive: true });

      sheet.addEventListener('touchmove', function (e) {
        if (!tracking) return;
        var d = e.touches[0].clientY - startY;

        if (!decided) {
          if (Math.abs(d) < 6) return;             /* wait for real intent */
          decided = true;
          dismissing = d > 0 && !(scroller && scroller.scrollTop > 0);
          if (!dismissing) { tracking = false; return; }
          sheet.classList.remove('is-settling');
        }

        if (d < 0) d = 0;
        delta = d;
        e.preventDefault();                        /* needs passive: false */
        sheet.style.transform = 'translateY(' + d + 'px)';
      }, { passive: false });

      var end = function () {
        if (!tracking || !dismissing) { reset(); return; }
        /* A quarter of the sheet, floored so a short sheet still needs a
           deliberate pull rather than a twitch. */
        var threshold = Math.max(70, sheet.getBoundingClientRect().height * 0.25);
        settle(delta > threshold);
        reset();
      };
      sheet.addEventListener('touchend', end);
      sheet.addEventListener('touchcancel', end);
    }

    /* ── Keyboard-aware sheet ────────────────────────────────────────
       On phones the input is pinned to the BOTTOM of the screen, so the
       soft keyboard would cover it. visualViewport reports the area the
       keyboard leaves behind; publish that inset as a custom property and
       lunai-mobile.css lifts the sheet by exactly that much. Desktop and
       browsers without visualViewport simply never set the var, and the
       CSS falls back to 0 — no layout depends on this running. */
    trackKeyboard() {
      var vv = window.visualViewport;
      if (!vv || this._vvBound) return;
      /* Published on <html>, not on this element: the sheet box that has to
         read them (.search-modal / .search-modal__content) is an ANCESTOR
         of <lunai-search>, and custom properties only inherit downwards. */
      var root = document.documentElement;
      this._vvBound = function () {
        var inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        root.style.setProperty('--lsheet-kb', inset + 'px');
        root.style.setProperty('--lsheet-vh', vv.height + 'px');
      };
      vv.addEventListener('resize', this._vvBound);
      vv.addEventListener('scroll', this._vvBound);
      this._vvBound();
    }

    untrackKeyboard() {
      var vv = window.visualViewport;
      if (!vv || !this._vvBound) return;
      vv.removeEventListener('resize', this._vvBound);
      vv.removeEventListener('scroll', this._vvBound);
      this._vvBound = null;
      document.documentElement.style.removeProperty('--lsheet-kb');
      document.documentElement.style.removeProperty('--lsheet-vh');
    }

    onInput() {
      var q = this.input.value.trim();
      if (this.clearBtn) this.clearBtn.hidden = q.length === 0;
      clearTimeout(this.timer);
      if (q.length < 2) { this.showEmpty(); return; }
      this.timer = setTimeout(function () { this.runSearch(q); }.bind(this), 180);
    }

    suggestUrl(q) {
      return shopRoot() + '/search/suggest'
        + '?q=' + encodeURIComponent(q)
        + '&section_id=' + encodeURIComponent(this.suggestSection)
        + '&resources[type]=query,collection,product'
        + '&resources[limit]=' + SUGGEST_LIMIT
        /* limit_scope defaults to "all": without "each" the 6 is shared
           across queries + collections + products, so a 10-harness term
           like "oprsnica" rendered only 2 product rows (3 completions +
           1 collection ate the rest). Per-type limit; the Liquid section
           caps completions (3) and collections (2) itself. */
        + '&resources[limit_scope]=each';
    }

    fullUrl(q) {
      return shopRoot() + '/search?q=' + encodeURIComponent(q)
        + '&type=product&section_id=' + encodeURIComponent(this.fullSection);
    }

    /* Whole-query synonym lookup. Measured dead ends this repairs, all
       verified against the live catalogue: ogrlica→ovratnica, miska→posoda,
       briketi→suha hrana, kakci→vrečke za iztrebke … The map lives in the
       locale file, so the owner extends it without a deploy. */
    rewrite(q) {
      return this.synonyms[fold(q)] || null;
    }

    /* Drop grammar words and generic dog words. Predictive search is blind
       to most multi-word Slovenian phrases ("hrana za pse" → 0 products
       where full text finds 84), because it matches a narrow field set
       rather than the description. Reducing to the noun recovers them —
       "hrana za pse" → "hrana" → 2 exact products.

       It also fixes the opposite failure: "za majhne pse" is nothing BUT
       grammar and dog words, and full text answered it with 87 products,
       essentially the whole catalogue posing as a result. Reduced, it finds
       nothing and the shopper gets an honest browse state instead. */
    reduce(q) {
      var words = q.toLowerCase().split(/\s+/).filter(Boolean);
      if (words.length < 2) return null;
      var core = words.filter(function (w) {
        var f = fold(w);
        return !this.stopwords[f] && !this.generic[f];
      }.bind(this));
      if (!core.length || core.length === words.length) return null;
      return core.join(' ');
    }

    fetchSection(url) {
      return fetch(url, { signal: this.controller.signal })
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var node = doc.querySelector('[data-lss]') || doc.querySelector('[data-lsr]');
          if (!node) return null;
          var n = node.getAttribute('data-lss-count');
          if (n === null) n = node.getAttribute('data-lsr-count');
          return { html: node.outerHTML, count: parseInt(n, 10) || 0 };
        });
    }

    /* The rescue ladder. Each rung runs ONLY when the one above came back
       empty, so an ordinary query still costs exactly one request. */
    runSearch(typed) {
      try { if (this.controller) this.controller.abort(); } catch (e) {}
      this.controller = new AbortController();
      this.classList.add('is-loading');

      var self = this;
      var used = this.rewrite(typed) || typed;

      /* rung 2: reduce, then re-check the map — "pasja ogrlica" only
         becomes "ovratnica" after "pasja" is out of the way */
      var core = this.reduce(used);
      if (core) core = this.rewrite(core) || core;

      /* Rungs 1 and 2 go out TOGETHER rather than one after the other.
         Predictive is known to miss most multi-word Slovenian, so the
         reduced term is the likely winner whenever it differs — waiting a
         full round-trip to discover that cost ~250ms on exactly the queries
         that were already slowest. The original still wins if it has
         anything, so precision is unchanged. Single-word queries produce no
         second request at all. */
      /* One rung failing must not take the other down with it — but an
         abort still has to propagate, or a superseded query would carry on
         and render results for a term the shopper has already edited. */
      var safe = function (p) {
        return p.catch(function (e) {
          if (e && e.name === 'AbortError') throw e;
          return null;
        });
      };
      var reduced = (core && core !== used)
        ? safe(this.fetchSection(this.suggestUrl(core)))
        : Promise.resolve(null);

      Promise.all([safe(this.fetchSection(this.suggestUrl(used))), reduced])
        .then(function (pair) {
          var r = pair[0], r2 = pair[1];
          if (r && r.count) return { r: r, term: used };
          if (r2 && r2.count) return { r: r2, term: core };
          return null;
        })
        .then(function (hit) {
          if (hit) return hit;
          /* rung 3: full text, on the REDUCED term. Measured: it beats the
             raw phrase ("pas za avto" 7 → "pas avto" 10) and it is what
             turns the 87-result noise case into an honest empty state. */
          var last = core || used;
          return self.fetchSection(self.fullUrl(last)).then(function (r3) {
            return (r3 && r3.count) ? { r: r3, term: last } : null;
          });
        })
        .then(function (hit) {
          if (!hit) { self.showNoResults(typed); return; }
          self.results.innerHTML = hit.r.html;
          self.showResults();
          self.showNote(typed, hit.term);
          self.idx = -1;
        })
        .catch(function (e) { if (e.name !== 'AbortError') console.warn('[lunai-search]', e); })
        .finally(function () { self.classList.remove('is-loading'); });
    }

    /* Say so when the answer is for a different word than the one typed —
       silently swapping the query would be the search lying about what it
       did, and the shopper needs the working word for next time. */
    showNote(typed, used) {
      if (!this.note) return;
      if (fold(used) === fold(typed) || !this.dataset.rewriteNote) {
        this.note.hidden = true;
        this.note.textContent = '';
        return;
      }
      this.note.textContent = this.dataset.rewriteNote
        .replace('%T%', typed).replace('%U%', used);
      this.note.hidden = false;
    }

    /* No dead end: every rung failed, so hand the shopper back the zero
       state — favourites, recent, moments — captioned with what missed. */
    showNoResults(typed) {
      this.results.hidden = true;
      this.results.innerHTML = '';
      this.empty.hidden = false;
      this.idx = -1;
      if (!this.note || !this.dataset.browseNote) return;
      this.note.textContent = this.dataset.browseNote.replace('%T%', typed);
      this.note.hidden = false;
    }

    showResults() { this.results.hidden = false; this.empty.hidden = true; }

    showEmpty() {
      this.results.hidden = true;
      this.results.innerHTML = '';
      this.empty.hidden = false;
      this.idx = -1;
      /* Clearing the box is not a failed search — drop any rescue caption
         so the zero state comes back clean. */
      if (this.note) { this.note.hidden = true; this.note.textContent = ''; }
    }

    onClick(e) {
      /* A completion or a recent term: put it in the box and re-run, so the
         shopper can keep editing rather than being navigated away. */
      var tag = e.target.closest('[data-lsr-term]');
      if (tag) {
        this.input.value = tag.getAttribute('data-lsr-term');
        this.onInput();
        this.input.focus();
        return;
      }
      if (e.target.closest('[data-lsr-row], [data-lsr-all], [data-lsr-go]')) {
        this.saveRecent(this.input.value.trim());
      }
    }

    onKey(e) {
      if (e.key === 'Escape') { this.close(); return; }
      var items = this.rows();
      if (e.key === 'Enter') {
        if (this.idx >= 0 && items[this.idx]) {
          e.preventDefault();
          this.saveRecent(this.input.value.trim());
          items[this.idx].click();
        } else {
          this.saveRecent(this.input.value.trim()); // let the form submit to /search
        }
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!items.length) return;
        e.preventDefault();
        this.idx = e.key === 'ArrowDown'
          ? Math.min(this.idx + 1, items.length - 1)
          : Math.max(this.idx - 1, 0);
        items.forEach(function (el, i) { el.classList.toggle('is-active', i === this.idx); }.bind(this));
        items[this.idx].scrollIntoView({ block: 'nearest' });
      }
    }

    rows() { return Array.prototype.slice.call(this.results.querySelectorAll('[data-lsr-row]')); }

    close() {
      /* Route through DetailsModal so body scroll-lock + trapFocus are
         released — flipping details.open directly leaked both. */
      var dm = this.closest('details-modal');
      if (dm && typeof dm.close === 'function') { dm.close(); return; }
      var d = this.closest('details');
      if (d) d.open = false;
    }

    getRecent() { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch (e) { return []; } }

    saveRecent(term) {
      if (!term || term.length < 2) return;
      var r = this.getRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      r.unshift(term);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(r.slice(0, 6))); } catch (e) {}
    }

    renderRecent() {
      if (!this.recentWrap) return;
      var r = this.getRecent();
      /* Empty the list as well as hiding it — "Počisti" left the old tags
         sitting in the DOM behind the hidden attribute, ready to reappear
         if anything ever un-hid the block. */
      if (!r.length) {
        this.recentWrap.hidden = true;
        this.recentTags.innerHTML = '';
        return;
      }
      this.recentWrap.hidden = false;
      this.recentTags.innerHTML = '';
      r.forEach(function (t, i) {
        var b = document.createElement('button');
        b.type = 'button';
        /* Same eight-pastel cycle as the moments, but worn as a dot rather
           than a fill — see lunai-search.css for why the two rows carry the
           palette differently. */
        b.className = 'lunai-search__tag lunai-search__tag--' + ((i % 8) + 1);
        b.setAttribute('data-lsr-term', t);
        b.textContent = t;          /* textContent, not innerHTML: recent terms are user input */
        this.recentTags.appendChild(b);
      }.bind(this));
    }

    getFavorites() { try { return JSON.parse(localStorage.getItem(FAV_KEY) || '[]'); } catch (e) { return []; } }

    getFavVariant(h) {
      try { return (JSON.parse(localStorage.getItem(FAV_VKEY) || '{}') || {})[h] || ''; } catch (e) { return ''; }
    }

    /* Favourites as compact cards. Same rule as the wishlist grid (R2.4):
       localStorage only holds handles, so each card is rendered in its own
       product context through the Section Rendering API — one card system,
       one money format. Order is preserved with placeholder slots because
       the fetches settle out of order. */
    renderFavorites() {
      var wrap = this.querySelector('[data-lsr-fav]');
      var row = this.querySelector('[data-lsr-fav-row]');
      if (!wrap || !row) return;
      var handles = this.getFavorites().slice(0, FAV_LIMIT);
      /* Same reset as renderRecent: un-hearting the last favourite has to
         drop the cards, and clear the cache key so re-adding one rebuilds
         instead of short-circuiting on a stale match. */
      if (!handles.length) {
        wrap.hidden = true;
        row.innerHTML = '';
        this._favKey = null;
        return;
      }
      /* Variant in the join key too: re-hearting a favourite in another
         colour keeps the same handle set, and a handle-only key would
         short-circuit the rebuild that shows the new colour. */
      var key = handles.map(function (h) { return h + ':' + this.getFavVariant(h); }.bind(this)).join(',');
      if (this._favKey === key) { wrap.hidden = false; return; }
      this._favKey = key;
      wrap.hidden = false;
      row.innerHTML = '';
      var root = shopRoot();
      handles.forEach(function (h) {
        var slot = document.createElement('div');
        slot.className = 'lunai-search__favslot';
        row.appendChild(slot);

        /* Measured 5 requests and 971ms of request time on every open, which
           made favourites the dominant cost of showing the sheet — more than
           the search itself. Cached per handle for the session, so opening
           the sheet a second time (or on the next page) costs nothing. Prices
           can go at most one session stale on this discovery surface; the
           product page a tap away is always live.
           &variant= renders the hearted colourway; the variant rides in the
           cache key (same scheme as lunai-favorites.js, which shares this
           cache) so a colour change never serves the old colour's card. */
        var vid = this.getFavVariant(h);
        var ck = FAVCACHE + h + (vid ? ':' + vid : '');
        var cached = null;
        try { cached = sessionStorage.getItem(ck); } catch (e) {}
        if (cached) { slot.innerHTML = cached; return; }

        fetch(root + '/products/' + encodeURIComponent(h) + '?section_id=lunai-search-fav' + (vid ? '&variant=' + encodeURIComponent(vid) : ''))
          .then(function (r) {
            if (r.ok) return r.text();
            /* 404 = product gone — hand it to the favourites engine so the
               stored list (and every badge) stops counting it. */
            if (r.status === 404 && window.LunaiFav && window.LunaiFav.prune) window.LunaiFav.prune(h);
            return null;
          })
          .then(function (html) {
            if (!html) { slot.remove(); return; }
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var payload = doc.querySelector('[data-lsfav-payload]');
            if (!payload || !payload.firstElementChild) {
              if (window.LunaiFav && window.LunaiFav.prune) window.LunaiFav.prune(h);
              slot.remove();
              return;
            }
            slot.innerHTML = payload.innerHTML;
            try { sessionStorage.setItem(ck, payload.innerHTML); } catch (e) {}
          })
          .catch(function () { slot.remove(); /* transient failure — drop the slot, keep the favourite */ });
      }, this);
    }
  }

  customElements.define('lunai-search', LunaiSearch);
})();
